import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  LinearProgress,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Lock as PrivateIcon,
  Public as PublicIcon,
  Assignment as TaskIcon,
  MenuBook as SourceIcon,
} from '@mui/icons-material';
import { ResearchNote, ResearchType, ResearchStatus, Priority, User } from '../../types';
import { formatDistanceToNow } from 'date-fns';

interface ResearchListProps {
  researchNotes: ResearchNote[];
  loading: boolean;
  currentUser: User;
  onResearchNoteClick: (note: ResearchNote) => void;
  onEditResearchNote: (note: ResearchNote) => void;
  onDeleteResearchNote: (noteId: string) => void;
  onApproveResearchNote: (noteId: string) => void;
  onRejectResearchNote: (noteId: string, reason: string) => void;
}

const ResearchList: React.FC<ResearchListProps> = ({
  researchNotes,
  loading,
  currentUser,
  onResearchNoteClick,
  onEditResearchNote,
  onDeleteResearchNote,
  onApproveResearchNote,
  onRejectResearchNote,
}) => {
  // Format research type for display
  const formatType = (type: ResearchType): string => {
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format status for display
  const formatStatus = (status: ResearchStatus): string => {
    return status
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get status color
  const getStatusColor = (status: ResearchStatus): 'success' | 'info' | 'warning' | 'default' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'info';
      case 'on-hold':
        return 'warning';
      case 'blocked':
        return 'warning';
      case 'not-started':
      default:
        return 'default';
    }
  };

  // Get priority color
  const getPriorityColor = (priority: Priority): 'error' | 'warning' | 'info' => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
      default:
        return 'info';
    }
  };

  // Get approval status color
  const getApprovalStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Check if user can edit note
  const canEdit = (note: ResearchNote): boolean => {
    return currentUser.role === 'admin' ||
           note.createdBy === currentUser.uid ||
           note.assignedTo.includes(currentUser.uid);
  };

  // Check if user can delete note
  const canDelete = (note: ResearchNote): boolean => {
    return currentUser.role === 'admin' || note.createdBy === currentUser.uid;
  };

  // Check if user can approve/reject
  const canModerate = currentUser.role === 'admin';

  return (
    <Box>
      {/* Research Grid */}
      {researchNotes.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            No research notes found matching the current filters.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {researchNotes.map((note) => (
            <Grid item xs={12} md={6} lg={4} key={note.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                  },
                }}
                onClick={() => onResearchNoteClick(note)}
              >
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  {/* Header with status and priority */}
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                    <Chip
                      label={formatStatus(note.status)}
                      size="small"
                      color={getStatusColor(note.status)}
                    />
                    <Chip
                      label={note.priority}
                      size="small"
                      color={getPriorityColor(note.priority)}
                      variant="outlined"
                    />
                    {note.isPrivate ? (
                      <Chip
                        icon={<PrivateIcon />}
                        label="Private"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Chip
                          icon={<PublicIcon />}
                          label="Shared"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {note.approvalStatus && (
                          <Chip
                            label={note.approvalStatus}
                            size="small"
                            color={getApprovalStatusColor(note.approvalStatus)}
                          />
                        )}
                      </>
                    )}
                  </Stack>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      minHeight: '3em',
                    }}
                  >
                    {note.title}
                  </Typography>

                  {/* Research type */}
                  <Chip
                    label={formatType(note.type)}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />

                  {/* Objective preview */}
                  {note.objective && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        mb: 2,
                      }}
                    >
                      {note.objective}
                    </Typography>
                  )}

                  {/* Progress bar */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {note.completionPercentage}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={note.completionPercentage}
                      color={note.completionPercentage === 100 ? 'success' : 'primary'}
                    />
                  </Box>

                  {/* Task and source counts */}
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TaskIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {note.tasks.filter(t => t.isCompleted).length}/{note.tasks.length} tasks
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <SourceIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {note.sources.length} source{note.sources.length !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Assigned users */}
                  {note.assignedTo.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Assigned to:
                      </Typography>
                      <AvatarGroup max={3} sx={{ justifyContent: 'flex-start' }}>
                        {note.assignedTo.map((userId, index) => (
                          <Avatar key={userId} sx={{ width: 24, height: 24, fontSize: 12 }}>
                            {index + 1}
                          </Avatar>
                        ))}
                      </AvatarGroup>
                    </Box>
                  )}

                  {/* Metadata */}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {note.researcherName || 'Unknown'} •{' '}
                    {formatDistanceToNow(new Date(note.createdAt))} ago
                  </Typography>
                </CardContent>

                {/* Actions */}
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onResearchNoteClick(note);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {canEdit(note) && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditResearchNote(note);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box>
                    {/* Approval actions (Admin only, shared notes only) */}
                    {canModerate && !note.isPrivate && note.approvalStatus === 'pending' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              onApproveResearchNote(note.id!);
                            }}
                          >
                            <ApproveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              const reason = window.prompt('Reason for rejection (optional):');
                              if (reason !== null) {
                                onRejectResearchNote(note.id!, reason);
                              }
                            }}
                          >
                            <RejectIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}

                    {/* Delete action */}
                    {canDelete(note) && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteResearchNote(note.id!);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ResearchList;
